import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi, money } from "../../lib/api";

/** Direct costs are hand-entered today. Every entry carries the shape the Foundation import
 *  will write later — date, vendor, reference, amount, source — so nothing has to be re-keyed. */
export default function DirectCostDialog({ project, row, canEdit, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ amount: "", cost_date: "", vendor: "", reference: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await opsApi.budgetDirectCosts(project.id, row.id)); }
    catch { toast.error("Could not load direct costs"); }
  }, [project.id, row.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      await opsApi.budgetAddDirectCost(project.id, row.id, { ...form, amount: Number(form.amount) });
      setForm({ amount: "", cost_date: "", vendor: "", reference: "", note: "" });
      toast.success("Direct cost added");
      await load();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that cost");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await opsApi.budgetDeleteDirectCost(project.id, id); await load(); onDone?.(); }
    catch { toast.error("Could not remove that cost"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
         onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-budget-direct-dialog">
        <h3 className="text-base font-semibold text-slate-900">
          Direct costs · {row.cost_code}.{row.cost_type}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          {row.cost_code_name} · {row.cost_type_name} · revised budget {money(row.revised_budget)}
        </p>

        {canEdit && (
          <div className="mt-4 grid gap-2 sm:grid-cols-5" data-testid="ops-budget-direct-form">
            <Input type="number" step="0.01" placeholder="Amount" value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   data-testid="ops-budget-direct-amount" />
            <Input type="date" value={form.cost_date}
                   onChange={(e) => setForm({ ...form, cost_date: e.target.value })}
                   data-testid="ops-budget-direct-date" />
            <Input placeholder="Vendor" value={form.vendor}
                   onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                   data-testid="ops-budget-direct-vendor" />
            <Input placeholder="Reference" value={form.reference}
                   onChange={(e) => setForm({ ...form, reference: e.target.value })}
                   data-testid="ops-budget-direct-reference" />
            <Button size="sm" disabled={busy || !form.amount} onClick={add}
                    data-testid="ops-budget-direct-add">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Add
            </Button>
          </div>
        )}

        <div className="mt-4 max-h-72 overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-1.5 text-left">Date</th>
                <th className="px-3 py-1.5 text-left">Vendor</th>
                <th className="px-3 py-1.5 text-left">Reference</th>
                <th className="px-3 py-1.5 text-left">Source</th>
                <th className="px-3 py-1.5 text-right">Amount</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!rows && <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-500">Loading…</td></tr>}
              {rows?.direct_costs?.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-500">
                  Nothing booked against this row yet.
                </td></tr>
              )}
              {rows?.direct_costs?.map((d) => (
                <tr key={d.id} data-testid={`ops-budget-direct-row-${d.id}`}>
                  <td className="px-3 py-1.5">{d.cost_date}</td>
                  <td className="px-3 py-1.5">{d.vendor || "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{d.reference || "—"}</td>
                  <td className="px-3 py-1.5">{d.source === "manual" ? "Manual" : "Foundation"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(d.amount)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {canEdit && (
                      <button onClick={() => remove(d.id)} data-testid={`ops-budget-direct-remove-${d.id}`}
                              className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-900" data-testid="ops-budget-direct-total">
            Total {money(rows?.total || 0)}
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
